/* eslint-disable react/jsx-props-no-spreading */
import mergeClasses from '../../../utils/shallowMerge';
import { useDevicesScreen } from '@srcDS/utils/useDevicesScreen';
import { ArrowLeft, ArrowRight } from '../../atoms/icons';
import CategoryTile from '../../molecules/categoryTile';
import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import Title from '../../typography/title';
import defaultClasses from './CategoriesSection.module.scss';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { StylesCustomizer } from '../../molecules/stylesCustomizer';

const CategoriesSectionFeaturedCarousel = ({
  categories,
  label,
  labelStyles,
  title,
  titleStyles,
  isArrowRight,
  withoutScroll,
  customClasses,
  customClassesForCategoryTile,
  tileLayout = 'stacked',
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);

  const breakpoints = {
    320: {
      spaceBetween: 16,
      slidesPerView: 1.2,
      initialSlide: 0,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
    },
    680: {
      spaceBetween: 12,
      slidesPerView: 2.3,
      initialSlide: 0,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 20,
    },
    980: {
      spaceBetween: 16,
      slidesPerView: 2.3,
      initialSlide: 0,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 40,
    },
  };
  const { isMobile } = useDevicesScreen();

  const checkFeatured = (items) => {
    return Array.from(items).find((item) => item.isFeatured);
  };

  const LabelText = (variables) => {
    return (
      <Text className={styles.cateogryLabel} as="span" weight="normal" textTransform="lowercase" variables={variables}>
        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </Text>
    );
  };
  const TitleText = (variables) => {
    return (
      <Heading
        align="center"
        className={styles.categoryHeader}
        as="h2"
        styleAs="h1"
        placement="plp"
        textTransform="lowercase"
        weight="semibold"
        variables={variables}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };
  const StyledLabelText = StylesCustomizer(LabelText);
  const StyledTitleText = StylesCustomizer(TitleText);

  const categoriesTitle =
    label || title ? (
      <Title className={styles.categoryTitle}>
        {label && <StyledLabelText styles={labelStyles} />}
        {title && <StyledTitleText styles={titleStyles} />}
      </Title>
    ) : null;

  const tiles = useCallback(
    (categories) => {
      return categories ? (
        <div
          className={clsx(styles.inner, {
            [` ${styles.sectionWithFeaturedCarousel}`]: checkFeatured(categories),
            [` ${styles.innerWithFeaturedCarousel}`]: !checkFeatured(categories),
          })}
        >
          {categories.map((cat, index) => {
            const pad = cat.isFeatured ? '100%' : '125%';
            const imageMd = cat.imageHeightMd || cat.imageHeightLg;
            const imageSm = cat.imageHeightsm ? cat.imageHeightsm : imageMd;
            return (
              <CategoryTile
                style={{
                  '--image-height-lg': cat.imageHeightLg ? `${cat.imageHeightLg}%` : pad,
                  '--image-height-md': imageMd ? `${imageMd}%` : pad,
                  '--image-height-sm': imageSm ? `${imageSm}%` : '133%',
                }}
                key={`category_${index}`}
                titleLink={cat.categoryUrl}
                align={cat.contentAlignment}
                title={cat.teaserText && cat.teaserText}
                description={cat.teaserDescription}
                titleStyles={cat.titleStyles}
                descriptionStyles={cat.descriptionStyles}
                image={cat.categoryImage}
                mobileImage={cat.mobileCategoryImage}
                links={cat.subcats && cat.subcats}
                isFeatured={cat.isFeatured}
                customClasses={{
                  ...customClassesForCategoryTile,
                  title: styles.title,
                }}
                linkObj={cat.categoryUrl}
                tileLayout={tileLayout}
              />
            );
          })}
        </div>
      ) : null;
    },
    [categories],
  );

  const carousel = useCallback(
    (categories) => (
      <div
        className={clsx(styles.categoriesCarousel, {
          [` ${styles.carouselWithFeatured}`]: checkFeatured(categories),
        })}
      >
        <Swiper
          className="homeCategorySwiper"
          modules={[Navigation, Scrollbar]}
          breakpoints={breakpoints}
          navigation={{
            prevEl: '.swiper-prev-arrow',
            nextEl: '.swiper-next-arrow',
          }}
          scrollbar={{
            el: '.swiper-scrollbar',
            draggable: true,
            dragSize: 62,
          }}
          centeredSlidesBounds={true}
        >
          {categories.map((cat, index) => {
            const imageMd = cat.imageHeightMd || cat.imageHeightLg;
            const imageSm = cat.imageHeightsm ? cat.imageHeightsm : imageMd;
            return (
              <SwiperSlide key={index} className={styles.wrapper}>
                <CategoryTile
                  style={{
                    '--image-height-lg': cat.imageHeightLg ? `${cat.imageHeightLg}%` : '125%',
                    '--image-height-md': imageMd ? `${imageMd}%` : '125%',
                    '--image-height-sm': imageSm ? `${imageSm}%` : '125%',
                  }}
                  titleLink={cat.categoryUrl}
                  linkObj={cat.categoryUrl}
                  align={cat.contentAlignment}
                  title={cat.teaserText && cat.teaserText}
                  description={cat.teaserDescription}
                  titleStyles={cat.titleStyles}
                  descriptionStyles={cat.descriptionStyles}
                  image={cat.categoryImage}
                  mobileImage={cat.mobileCategoryImage}
                  links={cat.subcats}
                  isFeatured={cat.isFeatured}
                  customClasses={{
                    ...customClassesForCategoryTile,
                    title: styles.title,
                  }}
                  tileLayout={tileLayout}
                />
              </SwiperSlide>
            );
          })}
          <div
            style={{
              display: isArrowRight ? 'block' : 'none',
            }}
          >
            <div
              data-carousel-arrow="arrow_left"
              className={`swiper-prev-arrow ${styles.arrowNavigation} ${styles.arrowNavigationLeft}`}
            >
              <ArrowLeft width="30px" height="30px" color="#000" />
            </div>
            <div
              data-carousel-arrow="arrow_right"
              className={`swiper-next-arrow ${styles.arrowNavigation} ${styles.arrowNavigationRight}`}
            >
              <ArrowRight width="30px" height="30px" color="#000" />
            </div>
          </div>
          {!withoutScroll && <div className={`swiper-scrollbar js-swiper-scrollbar ${styles.scrollbar}`} />}
        </Swiper>
      </div>
    ),
    [categories],
  );

  if (!categories || !Array.from(categories).length) return null;

  return (
    <div className={styles.popularCategoriesSection}>
      {isMobile && categoriesTitle}
      {isMobile && carousel(categories)}

      {!isMobile && (
        <div className={styles.featuredCarousel}>
          {tiles(Array.from(categories).filter((cat) => cat.isFeatured))}
          <div className={styles.carouselWrapper}>
            {categoriesTitle}
            {carousel(categories.filter((cat) => !cat.isFeatured))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesSectionFeaturedCarousel;
